import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

const KEY = "mee_cookie_prefs";

export default function CookieConsent() {
  const [decided, setDecided] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (!stored) {
      setDecided(false);
    } else {
      try { setAnalytics(JSON.parse(stored).analytics); } catch { /* ignore */ }
    }
    const open = () => { setModalOpen(true); };
    window.addEventListener("open-cookie-prefs", open);
    return () => window.removeEventListener("open-cookie-prefs", open);
  }, []);

  const persist = (analyticsOn) => {
    localStorage.setItem(KEY, JSON.stringify({ analytics: analyticsOn, ts: Date.now() }));
    setAnalytics(analyticsOn);
    setDecided(true);
    setModalOpen(false);
  };

  return (
    <>
      {/* Banner */}
      {!decided && !modalOpen && (
        <div data-testid="cookie-banner" className="fixed bottom-0 left-0 right-0 z-[60] bg-surface-elevated border-t border-border">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <p className="text-sm text-foreground/70 max-w-2xl leading-relaxed">
              We use cookies to operate our site and, optionally, to understand how content is used.{" "}
              <Link to="/legal/privacy" className="text-primary hover:underline">Privacy & Cookie Policy</Link>.
            </p>
            <div className="flex gap-2 shrink-0">
              <button data-testid="cookie-reject" onClick={() => persist(false)} className="border border-border px-4 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">Reject All</button>
              <button data-testid="cookie-customize" onClick={() => setModalOpen(true)} className="border border-border px-4 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">Customize</button>
              <button data-testid="cookie-accept" onClick={() => persist(true)} className="bg-primary text-primary-foreground px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-primary-hover transition-colors">Accept All</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-5" data-testid="cookie-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { if (localStorage.getItem(KEY)) setModalOpen(false); else persist(false); }} />
          <div className="relative w-full max-w-lg bg-surface-elevated border border-border p-7">
            <button onClick={() => { if (localStorage.getItem(KEY)) setModalOpen(false); else persist(false); }} className="absolute top-5 right-5 text-foreground/50 hover:text-foreground" data-testid="cookie-modal-close"><X className="h-5 w-5" /></button>
            <p className="mono-label">Cookie Preferences</p>
            <h2 className="mt-3 font-display text-2xl tracking-tight">Manage your cookies</h2>
            <p className="mt-3 text-sm text-foreground/60 leading-relaxed">
              We use cookies and similar technologies to operate our website, remember your privacy preferences, and understand how our content is used.
            </p>

            <div className="mt-6 space-y-4">
              <div className="border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-base font-medium">Essential Cookies <span className="font-mono text-[10px] uppercase tracking-widest text-primary ml-1">Always On</span></h3>
                  <p className="mt-1 text-xs text-foreground/55 leading-relaxed">Required for core site functionality, security, accessibility, and to remember your privacy choices.</p>
                </div>
                <div className="mt-1 h-5 w-9 rounded-full bg-primary/40 relative shrink-0"><span className="absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-primary" /></div>
              </div>
              <div className="border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-base font-medium">Analytics Cookies <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-1">Optional</span></h3>
                  <p className="mt-1 text-xs text-foreground/55 leading-relaxed">Help us understand how visitors interact with our site. Not used for advertising or cross-site tracking.</p>
                </div>
                <button data-testid="cookie-analytics-toggle" onClick={() => setAnalytics((v) => !v)} aria-pressed={analytics}
                  className={`mt-1 h-5 w-9 rounded-full relative shrink-0 transition-colors ${analytics ? "bg-primary/40" : "bg-border"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full transition-all ${analytics ? "right-0.5 bg-primary" : "left-0.5 bg-muted-foreground"}`} />
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button data-testid="cookie-save" onClick={() => persist(analytics)} className="bg-primary text-primary-foreground px-5 py-2.5 text-xs font-mono uppercase tracking-widest hover:bg-primary-hover transition-colors">Save Preferences</button>
              <button onClick={() => persist(true)} className="border border-border px-5 py-2.5 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">Accept All</button>
              <button onClick={() => persist(false)} className="border border-border px-5 py-2.5 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">Reject All</button>
            </div>
            <Link to="/legal/privacy" className="mt-5 inline-block text-xs text-primary hover:underline">Privacy & Cookie Policy</Link>
          </div>
        </div>
      )}
    </>
  );
}
